/**
 * The picture desk.
 *
 * Every photograph used on the public site is registered here once, with its
 * alt text and its caption, so a page never has to remember which file is
 * which. Captions read like photo credits because that is what they are —
 * short, factual, present tense, no selling.
 *
 * Three kinds of asset, and they behave differently in layout:
 *
 *   PLATES   full-bleed editorial frames. Rectangles. They run to the edge of
 *            the page and type sits on top of them.
 *   FACES    die-cut cutouts with a real alpha channel, one per HypeKind.
 *            These replaced the emoji chips. They are stuck on at an angle and
 *            overlap whatever is next to them. Never inside a box.
 *   OBJECTS  floating garments, same rules as faces.
 */

import type { HypeKind } from '@racksavant/core';

import edHero from '@racksavant/ui/assets/stickers/ed-hero.jpg';
import edStreet from '@racksavant/ui/assets/stickers/ed-street.jpg';
import edMirror from '@racksavant/ui/assets/stickers/ed-mirror.jpg';
import edDuo from '@racksavant/ui/assets/stickers/ed-duo.jpg';
import edGuy from '@racksavant/ui/assets/stickers/ed-guy.jpg';
import edGuyStreet from '@racksavant/ui/assets/stickers/ed-guy-street.jpg';
import edGroup from '@racksavant/ui/assets/stickers/ed-group.jpg';
import edDetail from '@racksavant/ui/assets/stickers/ed-detail.jpg';

import faceObsessed from '@racksavant/ui/assets/stickers/obsessed.png';
import faceUnreal from '@racksavant/ui/assets/stickers/unreal.png';
import faceSoftLaunch from '@racksavant/ui/assets/stickers/soft_launch.png';
import faceMenace from '@racksavant/ui/assets/stickers/menace.png';
import faceIconic from '@racksavant/ui/assets/stickers/iconic.png';
import faceCrying from '@racksavant/ui/assets/stickers/crying.png';
import faceNeedIt from '@racksavant/ui/assets/stickers/need_it.png';

import coatFloating from '@racksavant/ui/assets/stickers/coat-floating.png';
import dressFloating from '@racksavant/ui/assets/stickers/dress-floating.png';
import bootPair from '@racksavant/ui/assets/stickers/boot-pair.png';
import bagSlung from '@racksavant/ui/assets/stickers/bag-slung.png';
import jacketTossed from '@racksavant/ui/assets/stickers/jacket-tossed.png';
import sunglasses from '@racksavant/ui/assets/stickers/sunglasses.png';

export interface Plate {
  src: string;
  /** Described for someone who cannot see it. Clothes first, room second. */
  alt: string;
  /** Runs under the frame, set in the caption face. */
  caption: string;
  /** Natural aspect ratio, so the layout never jumps while it loads. */
  ratio: number;
}

export type PlateId =
  | 'hero'
  | 'street'
  | 'mirror'
  | 'duo'
  | 'guy'
  | 'guyStreet'
  | 'group'
  | 'detail';

export const PLATES: Record<PlateId, Plate> = {
  hero: {
    src: edHero,
    alt: 'A woman in an oversized oxblood wool coat over a black slip dress and knee-high boots, standing in a crowded bar.',
    caption: 'Oxblood over black. The coat is doing the talking.',
    ratio: 896 / 1200,
  },
  street: {
    src: edStreet,
    alt: 'A woman crossing a wet street at dusk in a long brown leather coat, dark knit and wide trousers.',
    caption: 'Leather to the ankle, crossing on the amber.',
    ratio: 896 / 1200,
  },
  mirror: {
    src: edMirror,
    alt: 'A woman photographing herself in a leaning full-length mirror, in a sheer grey long-sleeve and black wide-leg jeans.',
    caption: 'One photo, phone at chest height. That is the whole setup.',
    ratio: 848 / 1264,
  },
  duo: {
    src: edDuo,
    alt: 'Two friends laughing in a bedroom, one in a multicoloured sequin mini dress, one in an oatmeal overshirt and jeans.',
    caption: 'Getting ready, which was never a solo activity.',
    ratio: 896 / 1200,
  },
  guy: {
    src: edGuy,
    alt: 'A man sitting on concrete stairs in a grey wool suit, white t-shirt and battered black high-tops.',
    caption: 'Suit, tee, dead sneakers. Working exactly as intended.',
    ratio: 896 / 1200,
  },
  guyStreet: {
    src: edGuyStreet,
    alt: 'A man against a concrete wall in a distressed black leather biker jacket and wide black denim.',
    caption: 'The jacket has been worn in by somebody. Now it is his.',
    ratio: 848 / 1264,
  },
  group: {
    src: edGroup,
    alt: 'Three friends crowded into a mirrored lift, in a leather bomber, a black slip dress with an oxblood scarf, and an oxblood velvet blazer.',
    caption: 'Four floors, three opinions, one decision.',
    ratio: 896 / 1200,
  },
  detail: {
    src: edDetail,
    alt: 'Close crop of a woman fastening a heavy silver chain necklace over a black crumpled silk shirt.',
    caption: 'One line of metal at the collar, doing all the work.',
    ratio: 1,
  },
};

export interface Face {
  src: string;
  /** The reaction as a sentence, used for alt text and for the caption. */
  alt: string;
  label: string;
}

/** One photographed face per Hype kind. This is the reaction. */
export const FACES: Record<HypeKind, Face> = {
  obsessed: { src: faceObsessed, label: 'Obsessed', alt: 'A friend, delighted — obsessed.' },
  unreal: { src: faceUnreal, label: 'Unreal', alt: 'A friend, eyebrows up — unreal.' },
  soft_launch: {
    src: faceSoftLaunch,
    label: 'Soft launch',
    alt: 'A friend, half a smile — soft launch it.',
  },
  menace: { src: faceMenace, label: 'Menace', alt: 'A friend, tongue out — you are a menace.' },
  iconic: { src: faceIconic, label: 'Iconic', alt: 'A friend, chin up — iconic.' },
  crying: { src: faceCrying, label: 'Crying', alt: 'A friend, hands to face — crying about it.' },
  need_it: { src: faceNeedIt, label: 'Need it', alt: 'A friend, wide-eyed — needs it immediately.' },
};

export interface Cutout {
  src: string;
  alt: string;
  ratio: number;
}

export type CutoutId = 'coat' | 'dress' | 'boots' | 'bag' | 'jacket' | 'sunglasses';

/** Floating garments. They rotate a degree or two and overlap what is beneath. */
export const CUTOUTS: Record<CutoutId, Cutout> = {
  coat: {
    src: coatFloating,
    alt: 'An oxblood wool overcoat, cut out and floating.',
    ratio: 974 / 673,
  },
  dress: {
    src: dressFloating,
    alt: 'A dress, cut out and floating.',
    ratio: 933 / 649,
  },
  boots: {
    src: bootPair,
    alt: 'A pair of scuffed black leather riding boots, cut out.',
    ratio: 588 / 649,
  },
  bag: {
    src: bagSlung,
    alt: 'A slouched leather shoulder bag, cut out.',
    ratio: 441 / 714,
  },
  jacket: {
    src: jacketTossed,
    alt: 'A jacket mid-air, tossed, cut out.',
    ratio: 922 / 596,
  },
  sunglasses: {
    src: sunglasses,
    alt: 'A pair of scratched oval amber sunglasses, cut out.',
    ratio: 1111 / 517,
  },
};
